import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { 
    ChevronLeft, 
    Calendar, 
    Clock, 
    Users,
    User,
    Phone,
    Mail,
    MessageSquare,
    Check,
    CalendarCheck,
    PartyPopper,
    MapPin,
    Sun,
    Home,
    Sparkles,
    MessageCircle
} from 'lucide-react';

// ============================================================
// ALDRIN'S RESTO - PAGE DE RÉSERVATION AMÉLIORÉE
// Deux modules : Réservation de table + Réservation de salle
// ============================================================

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Logo officiel
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_resto-stock-manager-3/artifacts/0l7b747s_9F91CBF8-DC1E-49E7-9C24-96206851AEA7.PNG';

// Couleurs
const BRAND_GREEN = '#1a3d2c';
const GOLD = '#c9a962';
const WHATSAPP_GREEN = '#25D366';

// Numéro WhatsApp du restaurant (format international sans +)
const RESTAURANT_WHATSAPP = '24177712778'; // Numéro WhatsApp du restaurant

// Créneaux horaires
const TIME_SLOTS_LUNCH = ['11:30', '12:00', '12:30', '13:00', '13:30', '14:00'];
const TIME_SLOTS_DINNER = ['18:30', '19:00', '19:30', '20:00', '20:30', '21:00', '21:30', '22:00'];

// Options nombre de personnes (table)
const TABLE_GUEST_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8];

// Types d'événements
const EVENT_TYPES = [
    { id: 'mariage', label: 'Mariage' },
    { id: 'anniversaire', label: 'Anniversaire' },
    { id: 'bapteme', label: 'Baptême' },
    { id: 'communion', label: 'Communion' },
    { id: 'diner_honneur', label: "Dîner d'honneur" },
    { id: 'reunion', label: 'Réunion professionnelle' },
    { id: 'autre', label: 'Autre événement' }
];

// Espaces disponibles
const SPACES = [
    { id: 'terrasse', label: 'Terrasse', icon: Sun },
    { id: 'interieur', label: 'Intérieur', icon: Home }
];

// ============================================================
// COMPOSANTS RÉUTILISABLES
// ============================================================

const FormInput = ({ icon: Icon, label, error, required, ...props }) => (
    <div className="space-y-1.5">
        <label className="block text-sm font-medium text-gray-700">
            {label}
            {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
        <div className="relative">
            {Icon && (
                <Icon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-gray-400" />
            )}
            <input
                {...props}
                className={`w-full ${Icon ? 'pl-11' : 'pl-4'} pr-4 py-3 bg-white border ${error ? 'border-red-400 ring-1 ring-red-100' : 'border-gray-200'} rounded-xl text-gray-800 placeholder-gray-400 focus:outline-none focus:border-green-700 focus:ring-2 focus:ring-green-50 transition-all`}
            />
        </div>
        {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
);

const DateSelector = ({ selectedDate, onChange, minDate = new Date() }) => {
    const dates = [];
    for (let i = 0; i < 14; i++) {
        const date = new Date(minDate);
        date.setDate(date.getDate() + i);
        dates.push(date);
    }
    
    return (
        <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
                Date <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide -mx-1 px-1">
                {dates.map((date, i) => {
                    const isSelected = selectedDate?.toDateString() === date.toDateString();
                    const isToday = date.toDateString() === new Date().toDateString();
                    
                    return (
                        <button
                            key={i}
                            type="button"
                            onClick={() => onChange(date)}
                            className={`flex-shrink-0 flex flex-col items-center px-3 py-2.5 rounded-xl transition-all duration-200 min-w-[70px] ${
                                isSelected 
                                    ? 'text-white shadow-lg scale-105' 
                                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'
                            }`}
                            style={isSelected ? { backgroundColor: BRAND_GREEN } : {}}
                        >
                            <span className="text-[10px] uppercase font-medium opacity-70">
                                {date.toLocaleDateString('fr-FR', { weekday: 'short' })}
                            </span>
                            <span className="text-xl font-bold">{date.getDate()}</span>
                            <span className="text-[10px] opacity-70">
                                {date.toLocaleDateString('fr-FR', { month: 'short' })}
                            </span>
                            {isToday && (
                                <span className={`text-[9px] mt-0.5 font-medium ${isSelected ? 'text-white/80' : 'text-green-700'}`}>
                                    Aujourd'hui
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

const TimeSelector = ({ selectedTime, onChange, period = 'all' }) => {
    const times = period === 'lunch' ? TIME_SLOTS_LUNCH 
                : period === 'dinner' ? TIME_SLOTS_DINNER 
                : [...TIME_SLOTS_LUNCH, ...TIME_SLOTS_DINNER];
    
    return (
        <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
                Heure <span className="text-red-500">*</span>
            </label>
            
            {/* Périodes */}
            {period === 'all' && (
                <div className="flex gap-4 mb-3">
                    <div className="flex items-center gap-1.5 text-xs text-gray-500">
                        <Sun className="w-3.5 h-3.5" />
                        <span>Déjeuner</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-gray-500">
                        <span>|</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-gray-500">
                        <Sparkles className="w-3.5 h-3.5" />
                        <span>Dîner</span>
                    </div>
                </div>
            )}
            
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                {times.map(time => {
                    const isSelected = selectedTime === time;
                    const isLunch = TIME_SLOTS_LUNCH.includes(time);
                    
                    return (
                        <button
                            key={time}
                            type="button"
                            onClick={() => onChange(time)}
                            className={`py-2.5 px-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                                isSelected 
                                    ? 'text-white shadow-md' 
                                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'
                            }`}
                            style={isSelected ? { backgroundColor: BRAND_GREEN } : {}}
                        >
                            {time}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

const GuestSelector = ({ value, onChange, options = TABLE_GUEST_OPTIONS, label = "Nombre de personnes", required = true }) => (
    <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
            {label}
            {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
        <div className="flex gap-2 flex-wrap">
            {options.map(num => {
                const isSelected = value === num;
                return (
                    <button
                        key={num}
                        type="button"
                        onClick={() => onChange(num)}
                        className={`w-11 h-11 rounded-xl font-bold text-sm transition-all duration-200 ${
                            isSelected 
                                ? 'text-white shadow-md' 
                                : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'
                        }`}
                        style={isSelected ? { backgroundColor: BRAND_GREEN } : {}}
                    >
                        {num}
                    </button>
                );
            })}
            {options[options.length - 1] < 10 && (
                <button
                    type="button"
                    onClick={() => onChange(options[options.length - 1] + 1)}
                    className={`px-4 h-11 rounded-xl font-medium text-sm transition-all duration-200 ${
                        value > options[options.length - 1]
                            ? 'text-white shadow-md' 
                            : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'
                    }`}
                    style={value > options[options.length - 1] ? { backgroundColor: BRAND_GREEN } : {}}
                >
                    {value > options[options.length - 1] ? value : '9+'}
                </button>
            )}
        </div>
    </div>
);

const SpaceSelector = ({ value, onChange }) => (
    <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
            Espace préféré
        </label>
        <div className="grid grid-cols-2 gap-3">
            {SPACES.map(space => {
                const isSelected = value === space.id;
                const Icon = space.icon;
                return (
                    <button
                        key={space.id}
                        type="button"
                        onClick={() => onChange(space.id)}
                        className={`flex items-center justify-center gap-2 py-3.5 px-4 rounded-xl font-medium transition-all duration-200 ${
                            isSelected 
                                ? 'text-white shadow-md' 
                                : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'
                        }`}
                        style={isSelected ? { backgroundColor: BRAND_GREEN } : {}}
                    >
                        <Icon className="w-4.5 h-4.5" />
                        {space.label}
                    </button>
                );
            })}
        </div>
    </div>
);

// ============================================================
// GÉNÉRATION DU LIEN WHATSAPP
// ============================================================
const generateWhatsAppLink = (formData, type = 'table') => {
    const spaceLabel = formData.space === 'terrasse' ? 'en terrasse' 
                     : formData.space === 'interieur' ? 'en intérieur'
                     : formData.space === 'a_definir' ? '(espace à définir)'
                     : '';
    
    const dateStr = formData.date?.toLocaleDateString('fr-FR', { 
        weekday: 'long', 
        day: 'numeric', 
        month: 'long' 
    });
    
    let message = '';
    
    if (type === 'table') {
        message = `Bonjour, je confirme ma réservation chez Aldrin's Restaurant :\n\n`;
        message += `👤 Nom : ${formData.name}\n`;
        message += `👥 Personnes : ${formData.guests}\n`;
        message += `📅 Date : ${dateStr}\n`;
        message += `🕐 Heure : ${formData.time}\n`;
        if (spaceLabel) message += `📍 Espace : ${spaceLabel}\n`;
        if (formData.notes) message += `\n💬 Note : ${formData.notes}`;
    } else {
        const eventTypeLabel = EVENT_TYPES.find(t => t.id === formData.event_type)?.label || formData.event_type;
        message = `Bonjour, je souhaite confirmer ma demande de privatisation chez Aldrin's Restaurant :\n\n`;
        message += `🎉 Événement : ${eventTypeLabel}\n`;
        message += `👤 Nom : ${formData.name}\n`;
        message += `👥 Personnes : ${formData.guests}\n`;
        message += `📅 Date souhaitée : ${dateStr}\n`;
        if (formData.time) message += `🕐 Heure : ${formData.time}\n`;
        if (spaceLabel) message += `📍 Espace : ${spaceLabel}\n`;
        if (formData.message) message += `\n💬 Message : ${formData.message}`;
    }
    
    const encodedMessage = encodeURIComponent(message);
    return `https://wa.me/${RESTAURANT_WHATSAPP}?text=${encodedMessage}`;
};

// ============================================================
// MODULE 1: RÉSERVATION DE TABLE
// ============================================================

const TableReservationForm = ({ settings, onSuccess }) => {
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        email: '',
        date: null,
        time: '',
        guests: null,
        space: '',
        notes: ''
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    
    const validate = () => {
        const errs = {};
        if (!formData.name.trim()) errs.name = 'Nom requis';
        if (!formData.phone.trim()) errs.phone = 'Téléphone requis';
        if (!formData.date) errs.date = 'Date requise';
        if (!formData.time) errs.time = 'Heure requise';
        if (!formData.guests) errs.guests = 'Nombre de personnes requis';
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        
        setLoading(true);
        try {
            await axios.post(`${API_URL}/api/client/reservations`, {
                ...formData,
                date: formData.date?.toISOString().split('T')[0],
                type: 'table'
            });
            setSuccess(true);
            onSuccess?.();
        } catch (error) {
            setErrors({ submit: error.response?.data?.detail || 'Erreur lors de la réservation' });
        } finally {
            setLoading(false);
        }
    };
    
    if (success) {
        return (
            <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-12"
            >
                <div 
                    className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
                    style={{ backgroundColor: `${BRAND_GREEN}15` }}
                >
                    <Check className="w-10 h-10" style={{ color: BRAND_GREEN }} />
                </div>
                <h3 className="text-2xl font-semibold mb-3" style={{ color: BRAND_GREEN }}>
                    Réservation enregistrée !
                </h3>
                <p className="text-gray-600 mb-2">
                    Merci {formData.name}. Votre demande a bien été enregistrée.
                </p>
                <p className="text-gray-500 text-sm mb-6">
                    {formData.guests} personne{formData.guests > 1 ? 's' : ''} • {formData.date?.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })} à {formData.time}
                </p>
                
                {/* Bouton WhatsApp */}
                <div className="space-y-4 mb-6">
                    <p className="text-sm text-gray-600">
                        Confirmez votre réservation en un clic via WhatsApp :
                    </p>
                    <a
                        href={generateWhatsAppLink(formData, 'table')}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-3 px-8 py-4 rounded-2xl font-semibold text-white text-lg shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-300"
                        style={{ backgroundColor: WHATSAPP_GREEN }}
                    >
                        <MessageCircle className="w-6 h-6" />
                        Confirmer via WhatsApp
                    </a>
                    <p className="text-xs text-gray-400">
                        Un message pré-rempli s'ouvrira automatiquement
                    </p>
                </div>
                
                <button
                    onClick={() => {
                        setSuccess(false);
                        setFormData({ name: '', phone: '', email: '', date: null, time: '', guests: null, space: '', notes: '' });
                    }}
                    className="text-sm underline"
                    style={{ color: BRAND_GREEN }}
                >
                    Nouvelle réservation
                </button>
            </motion.div>
        );
    }
    
    return (
        <form onSubmit={handleSubmit} className="space-y-5">
            {/* Message d'accueil personnalisable */}
            {settings?.table_welcome_message && (
                <div className="p-4 rounded-xl bg-green-50 border border-green-100 mb-6">
                    <p className="text-sm text-green-800">{settings.table_welcome_message}</p>
                </div>
            )}
            
            {/* Date */}
            <DateSelector 
                selectedDate={formData.date} 
                onChange={(date) => setFormData({...formData, date})} 
            />
            
            {/* Heure */}
            <TimeSelector 
                selectedTime={formData.time} 
                onChange={(time) => setFormData({...formData, time})} 
            />
            
            {/* Nombre de personnes */}
            <GuestSelector 
                value={formData.guests} 
                onChange={(guests) => setFormData({...formData, guests})} 
            />
            {errors.guests && <p className="text-red-500 text-xs -mt-3">{errors.guests}</p>}
            
            {/* Espace */}
            <SpaceSelector 
                value={formData.space} 
                onChange={(space) => setFormData({...formData, space})} 
            />
            
            {/* Informations de contact */}
            <div className="pt-4 border-t border-gray-100 space-y-4">
                <h4 className="font-medium text-gray-800">Vos coordonnées</h4>
                
                <FormInput
                    icon={User}
                    label="Nom"
                    required
                    placeholder="Votre nom"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    error={errors.name}
                />
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FormInput
                        icon={Phone}
                        label="Téléphone"
                        required
                        type="tel"
                        placeholder="07 XX XX XX XX"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        error={errors.phone}
                    />
                    
                    <FormInput
                        icon={Mail}
                        label="Email"
                        type="email"
                        placeholder="votre@email.com"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                    />
                </div>
                
                <div className="space-y-1.5">
                    <label className="block text-sm font-medium text-gray-700">Demande particulière</label>
                    <textarea
                        rows={2}
                        placeholder="Anniversaire, allergie, chaise bébé..."
                        value={formData.notes}
                        onChange={(e) => setFormData({...formData, notes: e.target.value})}
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-800 placeholder-gray-400 focus:outline-none focus:border-green-700 focus:ring-2 focus:ring-green-50 transition-all resize-none"
                    />
                </div>
            </div>
            
            {errors.submit && (
                <p className="text-red-500 text-sm text-center">{errors.submit}</p>
            )}
            
            <button
                type="submit"
                disabled={loading}
                className="w-full py-4 rounded-xl font-semibold text-white transition-all duration-300 hover:shadow-lg disabled:opacity-50"
                style={{ backgroundColor: BRAND_GREEN }}
            >
                {loading ? 'Réservation en cours...' : 'Confirmer la réservation'}
            </button>
        </form>
    );
};

// ============================================================
// MODULE 2: RÉSERVATION DE SALLE / ÉVÉNEMENT
// ============================================================

const EventReservationForm = ({ settings, onSuccess }) => {
    const maxCapacity = settings?.event_max_capacity || 30;
    
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        email: '',
        date: null,
        time: '',
        guests: null,
        space: '',
        event_type: '',
        message: ''
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    
    const validate = () => {
        const errs = {};
        if (!formData.name.trim()) errs.name = 'Nom requis';
        if (!formData.phone.trim()) errs.phone = 'Téléphone requis';
        if (!formData.date) errs.date = 'Date requise';
        if (!formData.guests) errs.guests = 'Nombre de personnes requis';
        if (formData.guests > maxCapacity) errs.guests = `Maximum ${maxCapacity} personnes`;
        if (!formData.event_type) errs.event_type = 'Type d\'événement requis';
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        
        setLoading(true);
        try {
            await axios.post(`${API_URL}/api/client/reservations`, {
                ...formData,
                date: formData.date?.toISOString().split('T')[0],
                type: 'event'
            });
            setSuccess(true);
            onSuccess?.();
        } catch (error) {
            setErrors({ submit: error.response?.data?.detail || 'Erreur lors de la demande' });
        } finally {
            setLoading(false);
        }
    };
    
    if (success) {
        return (
            <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-12"
            >
                <div 
                    className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
                    style={{ backgroundColor: `${GOLD}20` }}
                >
                    <PartyPopper className="w-10 h-10" style={{ color: GOLD }} />
                </div>
                <h3 className="text-2xl font-semibold mb-3" style={{ color: BRAND_GREEN }}>
                    Demande enregistrée !
                </h3>
                <p className="text-gray-600 mb-2">
                    Merci {formData.name}. Votre demande a bien été enregistrée.
                </p>
                <p className="text-gray-500 text-sm mb-6">
                    {EVENT_TYPES.find(t => t.id === formData.event_type)?.label} • {formData.guests} personnes
                </p>
                
                {/* Bouton WhatsApp */}
                <div className="space-y-4 mb-6">
                    <p className="text-sm text-gray-600">
                        Confirmez votre demande en un clic via WhatsApp :
                    </p>
                    <a
                        href={generateWhatsAppLink(formData, 'event')}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-3 px-8 py-4 rounded-2xl font-semibold text-white text-lg shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-300"
                        style={{ backgroundColor: WHATSAPP_GREEN }}
                    >
                        <MessageCircle className="w-6 h-6" />
                        Confirmer via WhatsApp
                    </a>
                    <p className="text-xs text-gray-400">
                        Un message pré-rempli s'ouvrira automatiquement
                    </p>
                </div>
                
                <button
                    onClick={() => {
                        setSuccess(false);
                        setFormData({ name: '', phone: '', email: '', date: null, time: '', guests: null, space: '', event_type: '', message: '' });
                    }}
                    className="text-sm underline"
                    style={{ color: BRAND_GREEN }}
                >
                    Nouvelle demande
                </button>
            </motion.div>
        );
    }
    
    return (
        <form onSubmit={handleSubmit} className="space-y-5">
            {/* Message d'accueil personnalisable */}
            {settings?.event_welcome_message && (
                <div className="p-4 rounded-xl bg-amber-50 border border-amber-100 mb-6">
                    <p className="text-sm text-amber-800">{settings.event_welcome_message}</p>
                </div>
            )}
            
            {/* Type d'événement */}
            <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                    Type d'événement <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {EVENT_TYPES.map(type => {
                        const isSelected = formData.event_type === type.id;
                        return (
                            <button
                                key={type.id}
                                type="button"
                                onClick={() => setFormData({...formData, event_type: type.id})}
                                className={`py-3 px-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                                    isSelected 
                                        ? 'text-white shadow-md' 
                                        : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'
                                }`}
                                style={isSelected ? { backgroundColor: GOLD } : {}}
                            >
                                {type.label}
                            </button>
                        );
                    })}
                </div>
                {errors.event_type && <p className="text-red-500 text-xs">{errors.event_type}</p>}
            </div>
            
            {/* Date souhaitée */}
            <DateSelector 
                selectedDate={formData.date} 
                onChange={(date) => setFormData({...formData, date})}
                minDate={new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)} // Min 7 jours
            />
            
            {/* Heure souhaitée (optionnel) */}
            <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Heure souhaitée</label>
                <input
                    type="time"
                    value={formData.time}
                    onChange={(e) => setFormData({...formData, time: e.target.value})}
                    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-800 focus:outline-none focus:border-green-700 focus:ring-2 focus:ring-green-50 transition-all"
                />
            </div>
            
            {/* Nombre de personnes */}
            <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                    Nombre de personnes <span className="text-red-500">*</span>
                    <span className="text-gray-400 font-normal ml-2">(max {maxCapacity})</span>
                </label>
                <input
                    type="number"
                    min="1"
                    max={maxCapacity}
                    value={formData.guests || ''}
                    onChange={(e) => setFormData({...formData, guests: parseInt(e.target.value) || null})}
                    placeholder="Nombre de convives"
                    className={`w-full px-4 py-3 bg-white border ${errors.guests ? 'border-red-400' : 'border-gray-200'} rounded-xl text-gray-800 focus:outline-none focus:border-green-700 focus:ring-2 focus:ring-green-50 transition-all`}
                />
                {errors.guests && <p className="text-red-500 text-xs">{errors.guests}</p>}
            </div>
            
            {/* Espace */}
            <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Espace souhaité</label>
                <div className="grid grid-cols-3 gap-2">
                    {[...SPACES, { id: 'a_definir', label: 'À définir', icon: MapPin }].map(space => {
                        const isSelected = formData.space === space.id;
                        const Icon = space.icon;
                        return (
                            <button
                                key={space.id}
                                type="button"
                                onClick={() => setFormData({...formData, space: space.id})}
                                className={`flex flex-col items-center justify-center gap-1.5 py-3 px-2 rounded-xl text-xs font-medium transition-all duration-200 ${
                                    isSelected 
                                        ? 'text-white shadow-md' 
                                        : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'
                                }`}
                                style={isSelected ? { backgroundColor: BRAND_GREEN } : {}}
                            >
                                <Icon className="w-4 h-4" />
                                {space.label}
                            </button>
                        );
                    })}
                </div>
            </div>
            
            {/* Informations de contact */}
            <div className="pt-4 border-t border-gray-100 space-y-4">
                <h4 className="font-medium text-gray-800">Vos coordonnées</h4>
                
                <FormInput
                    icon={User}
                    label="Nom complet"
                    required
                    placeholder="Votre nom"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    error={errors.name}
                />
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FormInput
                        icon={Phone}
                        label="Téléphone"
                        required
                        type="tel"
                        placeholder="07 XX XX XX XX"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        error={errors.phone}
                    />
                    
                    <FormInput
                        icon={Mail}
                        label="Email"
                        type="email"
                        placeholder="votre@email.com"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                    />
                </div>
                
                <div className="space-y-1.5">
                    <label className="block text-sm font-medium text-gray-700">
                        Message / Demande particulière
                    </label>
                    <textarea
                        rows={4}
                        placeholder="Décrivez votre événement, vos besoins particuliers (décoration, menu spécial, horaires...)..."
                        value={formData.message}
                        onChange={(e) => setFormData({...formData, message: e.target.value})}
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-800 placeholder-gray-400 focus:outline-none focus:border-green-700 focus:ring-2 focus:ring-green-50 transition-all resize-none"
                    />
                </div>
            </div>
            
            {errors.submit && (
                <p className="text-red-500 text-sm text-center">{errors.submit}</p>
            )}
            
            <button
                type="submit"
                disabled={loading}
                className="w-full py-4 rounded-xl font-semibold text-white transition-all duration-300 hover:shadow-lg disabled:opacity-50"
                style={{ backgroundColor: GOLD }}
            >
                {loading ? 'Envoi en cours...' : 'Envoyer ma demande'}
            </button>
        </form>
    );
};

// ============================================================
// PAGE PRINCIPALE
// ============================================================

const ClientReservationPage = () => {
    const [activeTab, setActiveTab] = useState('table');
    const [settings, setSettings] = useState(null);
    
    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const response = await axios.get(`${API_URL}/api/client/settings`);
                setSettings(response.data);
            } catch (error) {
                console.log('Using default settings');
            }
        };
        fetchSettings();
    }, []);
    
    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
            {/* Header */}
            <header className="bg-white border-b border-gray-100">
                <div className="max-w-2xl mx-auto px-4 py-6">
                    <div className="flex items-center justify-between">
                        <Link 
                            to="/client" 
                            className="flex items-center gap-1.5 text-sm transition-colors hover:opacity-70"
                            style={{ color: BRAND_GREEN }}
                        >
                            <ChevronLeft className="w-4 h-4" />
                            Retour
                        </Link>
                        
                        <Link to="/client">
                            <img 
                                src={LOGO_URL} 
                                alt="Aldrin Restaurant" 
                                className="h-12 sm:h-14"
                            />
                        </Link>
                        
                        <div className="w-16" />
                    </div>
                </div>
            </header>
            
            {/* Contenu principal */}
            <main className="max-w-2xl mx-auto px-4 py-8">
                {/* Titre */}
                <div className="text-center mb-8">
                    <h1 
                        className="text-2xl sm:text-3xl font-semibold mb-2"
                        style={{ color: BRAND_GREEN }}
                    >
                        Réservation
                    </h1>
                    <p className="text-gray-500 text-sm">
                        Réservez votre table ou organisez votre événement
                    </p>
                </div>
                
                {/* Tabs */}
                <div className="flex gap-2 mb-8 p-1.5 bg-gray-100 rounded-2xl">
                    <button
                        onClick={() => setActiveTab('table')}
                        className={`flex-1 flex items-center justify-center gap-2 py-3.5 px-4 rounded-xl font-medium text-sm transition-all duration-300 ${
                            activeTab === 'table' 
                                ? 'bg-white text-gray-800 shadow-sm' 
                                : 'text-gray-500 hover:text-gray-700'
                        }`}
                    >
                        <CalendarCheck className="w-4.5 h-4.5" />
                        <span>Réserver une table</span>
                    </button>
                    <button
                        onClick={() => setActiveTab('event')}
                        className={`flex-1 flex items-center justify-center gap-2 py-3.5 px-4 rounded-xl font-medium text-sm transition-all duration-300 ${
                            activeTab === 'event' 
                                ? 'bg-white text-gray-800 shadow-sm' 
                                : 'text-gray-500 hover:text-gray-700'
                        }`}
                    >
                        <PartyPopper className="w-4.5 h-4.5" />
                        <span>Privatiser la salle</span>
                    </button>
                </div>
                
                {/* Formulaire */}
                <motion.div
                    key={activeTab}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white rounded-3xl shadow-lg border border-gray-100 p-6 sm:p-8"
                >
                    <AnimatePresence mode="wait">
                        {activeTab === 'table' ? (
                            <TableReservationForm settings={settings} />
                        ) : (
                            <EventReservationForm settings={settings} />
                        )}
                    </AnimatePresence>
                </motion.div>
                
                {/* Info contact */}
                <div className="text-center mt-8 text-sm text-gray-500">
                    <p>Des questions ? Appelez-nous au <a href="tel:+24177712778" className="font-medium" style={{ color: BRAND_GREEN }}>+241 77 71 27 78</a></p>
                </div>
            </main>
        </div>
    );
};

export default ClientReservationPage;
